/**
 * AI Service using Google Gemini
 * Provides intelligent assistance for fantasy cricket league
 */

import type { LeagueSquad, League, PlayerPoolEntry, SquadPlayer, StandingEntry } from '../types/database';
import { auth } from './firebase';

// NOTE: The Gemini API key is SERVER-SIDE ONLY. This service no longer talks to
// Gemini directly — it posts the league context to /api/ai-assistant with the
// signed-in user's Firebase ID token, and the server makes the Gemini call.
// (See api/ai-assistant.js. Prompt building lives there too.)

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface PeerSquad {
  squadName: string;
  rank: number;
  totalPoints: number;
  captainName: string;
  viceCaptainName: string;
  xFactorName: string;
  players: string[]; // player names only, keep tokens lean
}

export interface LeagueContext {
  leagueHasStarted: boolean;
  leagueRulesMarkdown?: string;
  userSquad: {
    userName: string;
    totalPoints: number;
    rank: number;
    // Playing XI — these are the scored players
    players: Array<{
      name: string;
      role: string;
      points: number;
      isCaptain: boolean;
      isViceCaptain: boolean;
      isXFactor: boolean;
      ownershipPercent: number;
    }>;
    // Bench players — in the squad but NOT in the playing XI
    // Can be swapped in via a Bench Transfer (see transferRules)
    benchPlayers: Array<{
      name: string;
      role: string;
      points: number;
      ownershipPercent: number;
    }>;
    bankedPoints: number;
    // Transfer summary
    flexibleTransfersRemaining: number;
    flexibleTransfersUsed: number;
    flexibleTransfersTotal: number;
    benchTransfersRemaining: number;
    benchTransfersUsed: number;
    benchTransfersTotal: number;
  };
  // Transfer rules — always include so Gemini understands what each transfer type does
  transferRules: {
    benchTransferExplained: string;
    flexibleTransferExplained: string;
    benchSlotsCount: number;
  };
  leagueStats: {
    totalPlayers: number;
    averagePoints: number;
    topScore: number;
    userRank: number;
  };
  availablePlayers: Array<{
    name: string;
    role: string;
    points: number;
    team: string;
    isOverseas: boolean;
  }>;
  // Peer intelligence — always included, token-light
  peerIntelligence: {
    top3Squads: PeerSquad[];         // Top 3 squads compressed
    captainDistribution: Array<{ name: string; count: number; percent: number }>; // Top 5 captain picks across league
    // Per-player ownership is already on userSquad.players.ownershipPercent
  };
  // On-demand: full squad of a specific peer for head-to-head
  comparisonSquad?: PeerSquad & { players: string[] };
}

/**
 * Compress a squad into a lean PeerSquad for the prompt
 */
function compressToPeerSquad(squad: LeagueSquad, rank: number): PeerSquad {
  const captainPlayer = squad.players.find((p: SquadPlayer) => p.playerId === squad.captainId);
  const vcPlayer = squad.players.find((p: SquadPlayer) => p.playerId === squad.viceCaptainId);
  const xfPlayer = squad.players.find((p: SquadPlayer) => p.playerId === squad.xFactorId);
  return {
    squadName: squad.squadName || 'Unknown Squad',
    rank,
    totalPoints: squad.totalPoints || 0,
    captainName: captainPlayer?.playerName || 'Unknown',
    viceCaptainName: vcPlayer?.playerName || 'Unknown',
    xFactorName: xfPlayer?.playerName || 'Unknown',
    players: squad.players.map((p: SquadPlayer) => p.playerName),
  };
}

/**
 * Build context from user data
 * standings: from the latest LeaderboardSnapshot — these are the AUTHORITATIVE
 *   rank and totalPoints values (include predictionBonusPoints, powerplayPoints, etc.)
 *   If no snapshot exists yet, pass [] and we fall back to squad docs.
 */
export function buildLeagueContext(
  userSquad: LeagueSquad,
  allSquads: LeagueSquad[],
  playerPool: PlayerPoolEntry[],
  league: League,
  standings: StandingEntry[] = []
): LeagueContext {
  const totalLeaguePlayers = allSquads.length;
  const leagueHasStarted = new Date() >= new Date(league.startDate);

  // Use snapshot standings as authoritative rank/points source.
  // Standings are already sorted by rank from the snapshot service.
  const userStanding = standings.find(s => s.userId === userSquad.userId);
  const userRank = userStanding?.rank ?? userSquad.rank ?? 1;
  const userTotalPoints = userStanding?.totalPoints ?? userSquad.totalPoints ?? 0;
  const averagePoints = standings.length > 0
    ? standings.reduce((sum, s) => sum + s.totalPoints, 0) / standings.length
    : allSquads.reduce((sum, s) => sum + (s.totalPoints || 0), 0) / (totalLeaguePlayers || 1);
  const topScore = standings.length > 0
    ? standings[0].totalPoints  // standings[0] = rank #1
    : Math.max(...allSquads.map(s => s.totalPoints || 0));

  // Sort all squads by snapshot rank for peer ordering
  const sortedSquads = standings.length > 0
    ? [...standings].sort((a, b) => a.rank - b.rank)
        .map(s => allSquads.find(sq => sq.userId === s.userId))
        .filter((sq): sq is LeagueSquad => !!sq)
    : [...allSquads].sort((a, b) => (a.rank || 999) - (b.rank || 999));

  // ---- Transfer counts ----
  const flexibleTotal = league.transferTypes?.flexibleTransfers?.maxAllowed || 0;
  const flexibleUsed = userSquad.flexibleTransfersUsed || 0;
  const flexibleRemaining = Math.max(0, flexibleTotal - flexibleUsed);

  const benchTotal = league.transferTypes?.benchTransfers?.maxAllowed || 0;
  const benchUsed = userSquad.benchTransfersUsed || 0;
  const benchRemaining = Math.max(0, benchTotal - benchUsed);
  const benchSlots = league.transferTypes?.benchTransfers?.benchSlots || 0;

  // ---- Bench vs playing XI split ----
  // Playing XI = first 11 players; bench = remainder (benchSlots count)
  const playingXI = userSquad.players.slice(0, 11);
  const benchPlayersList = userSquad.players.slice(11);

  // ---- Ownership % per player ----
  const ownershipMap = new Map<string, number>();
  allSquads.forEach(squad => {
    squad.players.forEach((p: SquadPlayer) => {
      ownershipMap.set(p.playerId, (ownershipMap.get(p.playerId) || 0) + 1);
    });
  });

  // ---- Captain distribution ----
  const captainMap = new Map<string, { name: string; count: number }>();
  allSquads.forEach(squad => {
    if (squad.captainId) {
      const captainPlayer = squad.players.find((p: SquadPlayer) => p.playerId === squad.captainId);
      const name = captainPlayer?.playerName || 'Unknown';
      const existing = captainMap.get(squad.captainId);
      if (existing) {
        existing.count++;
      } else {
        captainMap.set(squad.captainId, { name, count: 1 });
      }
    }
  });
  const captainDistribution = Array.from(captainMap.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map(c => ({
      name: c.name,
      count: c.count,
      percent: Math.round((c.count / (totalLeaguePlayers || 1)) * 100),
    }));

  // ---- Top 3 peer squads (authoritative rank/points from snapshot) ----
  const top3Squads = sortedSquads
    .filter(s => s.userId !== userSquad.userId)
    .slice(0, 3)
    .map((squad) => {
      const standing = standings.find(s => s.userId === squad.userId);
      const rank = standing?.rank ?? squad.rank ?? 0;
      const peer = compressToPeerSquad(squad, rank);
      // Override totalPoints with snapshot value if available
      if (standing) peer.totalPoints = standing.totalPoints;
      return peer;
    });

  return {
    leagueHasStarted,
    leagueRulesMarkdown: buildLeagueRulesMarkdown(league),
    userSquad: {
      userName: userSquad.squadName || 'Unknown Squad',
      totalPoints: userTotalPoints,
      rank: userRank,
      players: playingXI.map((p: SquadPlayer) => ({
        name: p.playerName,
        role: p.role,
        points: p.points,
        isCaptain: p.playerId === userSquad.captainId,
        isViceCaptain: p.playerId === userSquad.viceCaptainId,
        isXFactor: p.playerId === userSquad.xFactorId,
        ownershipPercent: Math.round(((ownershipMap.get(p.playerId) || 0) / (totalLeaguePlayers || 1)) * 100),
      })),
      benchPlayers: benchPlayersList.map((p: SquadPlayer) => ({
        name: p.playerName,
        role: p.role,
        points: p.points,
        ownershipPercent: Math.round(((ownershipMap.get(p.playerId) || 0) / (totalLeaguePlayers || 1)) * 100),
      })),
      bankedPoints: userSquad.bankedPoints || 0,
      flexibleTransfersRemaining: flexibleRemaining,
      flexibleTransfersUsed: flexibleUsed,
      flexibleTransfersTotal: flexibleTotal,
      benchTransfersRemaining: benchRemaining,
      benchTransfersUsed: benchUsed,
      benchTransfersTotal: benchTotal,
    },
    transferRules: {
      benchSlotsCount: benchSlots,
      benchTransferExplained: `A Bench Transfer lets you swap any player in your Playing XI with one of your ${benchSlots} bench players. You can also use it to reassign Captain/VC/XF roles. You have ${benchRemaining} of ${benchTotal} bench transfers remaining. Bench players do NOT score points until they are moved into the Playing XI via a bench transfer.`,
      flexibleTransferExplained: `A Flexible Transfer lets you replace any non-Captain player in your Playing XI with any player from the global player pool. You can also use it to reassign VC/XF roles (but NOT the Captain). You have ${flexibleRemaining} of ${flexibleTotal} flexible transfers remaining.`,
    },
    leagueStats: {
      totalPlayers: totalLeaguePlayers,
      averagePoints,
      topScore,
      userRank,
    },
    availablePlayers: playerPool.map(p => ({
      name: p.name,
      role: p.role,
      points: p.points,
      team: p.team,
      isOverseas: p.isOverseas,
    })),
    peerIntelligence: {
      top3Squads,
      captainDistribution,
    },
  };
}

/**
 * Inject a specific peer squad for head-to-head comparison
 */
export function injectComparisonSquad(
  context: LeagueContext,
  peerSquad: LeagueSquad,
  peerRank: number
): LeagueContext {
  return {
    ...context,
    comparisonSquad: compressToPeerSquad(peerSquad, peerRank),
  };
}

/**
 * Build a clean markdown block describing the league's rules and config.
 * Injected into the Stumpy system prompt for accurate rule answering.
 */
export function buildLeagueRulesMarkdown(league: League): string {
  const transfers = league.transferTypes;
  const rules = league.squadRules || {};
  const lines: string[] = [
    `## League Rules: ${league.name}`,
    `- **Format:** ${league.format}`,
    `- **Tournament:** ${league.tournamentName || 'N/A'}`,
    `- **Squad Size:** ${league.squadSize} players (min ${rules.minBatsmen || 0} batsmen, ${rules.minBowlers || 0} bowlers, ${rules.minWicketkeepers || 0} wicketkeepers)`,
    `- **Role Multipliers:** Captain 2x | Vice-Captain 1.5x | X-Factor 1.25x`,
  ];

  // Transfers
  if (transfers?.benchTransfers?.enabled) lines.push(`- **Bench Transfers:** ${transfers.benchTransfers.maxAllowed} total (${transfers.benchTransfers.benchSlots} bench slots) — swap Playing XI ↔ bench`);
  if (transfers?.flexibleTransfers?.enabled) lines.push(`- **Flexible Transfers:** ${transfers.flexibleTransfers.maxAllowed} total — replace any non-captain with a player from the pool`);

  // Special features
  lines.push(`- **Hidden Player:** ${league.hiddenPlayerEnabled ? 'Enabled — each participant picks one secret 12th player; identity hidden from opponents; points added at season end' : 'Disabled'}`);
  lines.push(`- **Powerplay:** ${league.powerplayEnabled ? `Enabled (${league.maxPowerplayMatches || 1} powerplay match${(league.maxPowerplayMatches || 1) > 1 ? 'es' : ''}, ${(league.ppMatchMode ?? 'fixed') === 'activation' ? 'activation mode — activate any time before deadline' : 'fixed selection — choose before squad deadline'})` : 'Disabled'}`);

  // Overseas
  if (rules.overseasPlayersEnabled) lines.push(`- **Overseas Players:** Max ${rules.maxOverseasPlayers || 4} per squad`);

  return lines.join('\n');
}

/**
 * Query the AI assistant.
 *
 * Posts the league context to the server endpoint with the signed-in user's
 * Firebase ID token. The Gemini key never reaches the browser; the server
 * verifies the user, builds the prompt, and calls Gemini.
 */
export async function queryAI(
  question: string,
  context: LeagueContext,
  conversationHistory: Message[] = []
): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Please sign in to use the assistant.');
  }

  let idToken: string;
  try {
    idToken = await user.getIdToken();
  } catch {
    throw new Error('Could not verify your session. Please sign in again.');
  }

  let res: Response;
  try {
    res = await fetch('/api/ai-assistant', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ question, context, conversationHistory }),
    });
  } catch {
    throw new Error('Network error reaching the assistant. Please try again.');
  }

  if (!res.ok) {
    let detail = '';
    try {
      const err = await res.json();
      detail = err?.error || '';
    } catch {
      /* non-JSON error body */
    }
    if (res.status === 401) throw new Error('Your session expired. Please sign in again.');
    if (res.status === 429) throw new Error(detail || 'AI is busy right now — please try again in a bit.');
    throw new Error(detail || `Failed to get AI response (status ${res.status}). Please try again.`);
  }

  const data = await res.json();
  if (!data?.text) {
    throw new Error('The assistant returned an empty response. Please try again.');
  }
  return data.text;
}

/**
 * Quick suggestions for pre-league (before start date)
 */
export const PRE_LEAGUE_SUGGESTED_QUESTIONS = [
  "How does the scoring work?",
  "What is the X-Factor multiplier?",
  "How do bench transfers work?",
  "What is the Hidden Player feature?",
];

/**
 * Quick suggestions for common queries
 */
export const SUGGESTED_QUESTIONS = [
  "Stumpy, how do I topple the league leader?",
  "What are my differentials?",
  "Who should I make my captain?",
  "Suggest 3 transfers to improve my squad",
  "Am I at risk of dropping ranks?",
  "Analyse the captain picks across the league",
  "Which players are underperforming?",
  "How unique is my squad compared to the rest?",
];

/**
 * Get a smart suggestion based on context
 */
export function getSmartSuggestion(context: LeagueContext): string {
  const { userSquad, leagueStats } = context;

  // If below average, suggest transfers
  if (userSquad.totalPoints < leagueStats.averagePoints) {
    return "How can I improve my squad to catch up?";
  }

  // If no transfers remaining
  if (userSquad.flexibleTransfersRemaining === 0 && userSquad.benchTransfersRemaining === 0) {
    return "What captain should I choose for maximum points?";
  }

  // If in top 3, suggest how to maintain lead
  if (userSquad.rank <= 3) {
    return "Am I at risk of dropping ranks?";
  }

  // Close to a rival — suggest comparison
  if (userSquad.rank <= 5) {
    return "How do I beat the league leader?";
  }

  // Default
  return "What are my differentials?";
}
